import React, { useCallback, useEffect, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { Button, Modal, ModalFooter } from "@telluscompany/design-system";
import { useLocalization } from "@i18n";
import { LoadingBlurDiv } from "@shared/components";
import {
  DEFAULT_PAGE_SIZE,
  EMPTY_PAGINATION_META,
  type PageSize,
} from "@shared/constants/pagination.constants";
import { useGeneralStructureReservoirManagementQuery } from "../../api/generalReservoirManagement/generalReservoirManagement.queries";
import type { GeneralReservoirManagementFilters } from "../../api/generalReservoirManagement/generalReservoirManagement.types";
import { formatGeneralDisplayValue } from "../../utils/formatGeneralDisplayValue";
import {
  mapReservoirManagementSortToApi,
  type GeneralDashboardTableSort,
} from "../../utils/generalDashboardTableSort";
import { GeneralDashboardPaginatedTable } from "../GeneralDashboardPaginatedTable";
import { GeneralReservoirManagementFilter } from "../filters/GeneralReservoirManagementFilter";

interface GeneralReservoirManagementModalProps {
  open: boolean;
  onClose: () => void;
  structureId: string | null;
}

const RESERVOIR_MANAGEMENT_TABLE_ID = "general-dashboard-reservoir-management-table";

export const GeneralReservoirManagementModal: React.FC<
  GeneralReservoirManagementModalProps
> = ({ open, onClose, structureId }) => {
  const { translate } = useLocalization();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState<PageSize>(DEFAULT_PAGE_SIZE);
  const [appliedFilters, setAppliedFilters] = useState<
    Pick<GeneralReservoirManagementFilters, "recordedOn">
  >({});
  const [tableSort, setTableSort] = useState<GeneralDashboardTableSort | null>(
    null,
  );

  useEffect(() => {
    if (open) {
      setPage(1);
      setLimit(DEFAULT_PAGE_SIZE);
      setAppliedFilters({});
      setTableSort(null);
    }
  }, [open, structureId]);

  const queryFilters = useMemo(
    () => ({
      ...appliedFilters,
      page,
      limit,
      ...mapReservoirManagementSortToApi(tableSort),
    }),
    [appliedFilters, limit, page, tableSort],
  );

  const { data, isFetching, isError } = useGeneralStructureReservoirManagementQuery(
    open && structureId ? structureId : undefined,
    queryFilters,
  );

  const handleFilter = useCallback(
    (filters: Pick<GeneralReservoirManagementFilters, "recordedOn">) => {
      setAppliedFilters(filters);
      setPage(1);
    },
    [],
  );

  const handleClearFilter = useCallback(() => {
    setAppliedFilters({});
    setPage(1);
  }, []);

  const handleSortChange = useCallback<
    Dispatch<SetStateAction<GeneralDashboardTableSort | null>>
  >((value) => {
    setTableSort((current) => {
      const next =
        typeof value === "function"
          ? value(current ?? { key: "", direction: "asc" })
          : value;
      return next;
    });
    setPage(1);
  }, []);

  const tableFilter = useCallback(
    () => (
      <GeneralReservoirManagementFilter
        onFilter={handleFilter}
        onClear={handleClearFilter}
        initialFilters={{
          recordedOn: appliedFilters.recordedOn,
        }}
      />
    ),
    [appliedFilters.recordedOn, handleClearFilter, handleFilter],
  );

  const tableColumns = useMemo(
    () => [
      {
        key: "recordedOnDisplay",
        label: translate("admin.structures.reservoirManagement.label.recordedOn"),
        minWidth: "120px",
        sortType: "date" as const,
      },
      {
        key: "occupiedArea",
        label: translate(
          "admin.structures.reservoirManagement.label.occupiedAreaShort",
        ),
        minWidth: "140px",
        sortType: "number" as const,
      },
      {
        key: "totalVolume",
        label: translate(
          "admin.structures.reservoirManagement.label.totalVolumeShort",
        ),
        minWidth: "120px",
        sortType: "number" as const,
      },
      {
        key: "totalOccupiedVolume",
        label: translate(
          "admin.structures.reservoirManagement.label.totalOccupiedVolumeShort",
        ),
        minWidth: "160px",
        sortType: "number" as const,
      },
      {
        key: "availableVolume",
        label: translate(
          "admin.structures.reservoirManagement.label.availableVolumeShort",
        ),
        minWidth: "140px",
        sortType: "number" as const,
      },
      {
        key: "rejectOccupiedVolume",
        label: translate(
          "admin.structures.reservoirManagement.label.rejectOccupiedVolumeShort",
        ),
        minWidth: "160px",
        sortType: "number" as const,
      },
      {
        key: "waterOccupiedVolume",
        label: translate(
          "admin.structures.reservoirManagement.label.waterOccupiedVolumeShort",
        ),
        minWidth: "180px",
        sortType: "number" as const,
      },
      {
        key: "waterLevel",
        label: translate("admin.structures.reservoirManagement.label.waterLevel"),
        minWidth: "120px",
        sortType: "number" as const,
      },
    ],
    [translate],
  );

  const tableData = useMemo(
    () =>
      (data?.data ?? []).map((item) => ({
        ...item,
        recordedOnDisplay: formatGeneralDisplayValue(item.recordedOn, true),
        occupiedArea: formatGeneralDisplayValue(item.occupiedArea),
        totalVolume: formatGeneralDisplayValue(item.totalVolume),
        totalOccupiedVolume: formatGeneralDisplayValue(item.totalOccupiedVolume),
        availableVolume: formatGeneralDisplayValue(item.availableVolume),
        rejectOccupiedVolume: formatGeneralDisplayValue(item.rejectOccupiedVolume),
        waterOccupiedVolume: formatGeneralDisplayValue(item.waterOccupiedVolume),
        waterLevel: formatGeneralDisplayValue(item.waterLevel),
      })),
    [data?.data],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={translate("general.dashboard.reservoirManagement.modalTitle")}
      content={
        <LoadingBlurDiv isLoading={isFetching}>
          <div className="general-dashboard-modal-body general-dashboard-modal-body--table">
            {isError && (
              <p className="text-sm text-destructive">
                {translate("general.dashboard.loadError")}
              </p>
            )}

            <GeneralDashboardPaginatedTable
              tableId={RESERVOIR_MANAGEMENT_TABLE_ID}
              tableColumns={tableColumns}
              tableData={tableData}
              meta={data?.meta ?? EMPTY_PAGINATION_META}
              isLoading={isFetching}
              onPageChange={setPage}
              onLimitChange={(nextLimit) => {
                setLimit(nextLimit);
                setPage(1);
              }}
              onSortChange={handleSortChange}
              tableFilter={tableFilter}
              filterButtonText={translate(
                "admin.structures.reservoirManagement.table.filterButtonText",
              )}
            />
          </div>
          <ModalFooter
            footer={
              <Button type="button" variant="primary" onClick={onClose}>
                {translate("general.dashboard.button.close")}
              </Button>
            }
          />
        </LoadingBlurDiv>
      }
    />
  );
};
